import { faCogs } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { FC } from 'react'
import { FightEvent, FightSegment } from 'web/data/models'
import { useToggle } from 'web/hooks/useToggle'
import { dataTest } from 'web/utils/test-utils'
import { secondsToTimestamp } from 'web/utils/time-utils'
import { DivBtn } from '../generic/DivBtn'
import { WithTooltip } from '../generic/WithTooltip'
import { AddEventInputs, AddNewEventCallback } from './AddEventInputs'
import { AddEventToggleTab } from './AddEventToggleTab'
import { EditSegmentTab } from './EditSegmentTab'
import { FightEventItem, FightEventItemProps } from './FightEventItem'
import { NoEventsMessage } from './NoEventsMessage'

export type FightSegmentItemProps = {
    /**
     * Setting this to false will disable the ability to edit the segment as
     * well.
     */
    showSegmentHeader: boolean

    segment: FightSegment
    events: FightEvent[]

    decorateNameWithOrdinal: (event: FightEvent) => string

    eventProps: Omit<FightEventItemProps, "fightEvent">

    /**
     * Indicates that we're in full outline edit mode.
     */
    outlineEditMode?: {
        onAddEvent: AddNewEventCallback

        onAdd: (segmentId: string) => void
        onEdit: (segmentId: string) => void

        blockChildSegment: (segmentId: string) => boolean
    }

    /**
     * Indicates that we're editing the outline from the setup edit
     * mode. We have reduced capabilities here.
     */
    setupEditMode?: {
        onEditEvent: (eventId: string, segmentId: string) => void
        onAddSetupEvent: AddNewEventCallback
    }
}

export const FightSegmentItem: FC<FightSegmentItemProps> = (props) => {

    const [addEventVisible, toggleAddEventVisible] = useToggle(
        props.setupEditMode == null &&
        props.events.filter(e => e.userCreated).length <= 0
    )

    const {
        segment, events, eventProps,
        outlineEditMode, setupEditMode,
        showSegmentHeader, decorateNameWithOrdinal
    } = props

    let onAddHandler: AddNewEventCallback = () => {
        return undefined
    }

    if (outlineEditMode?.onAdd) {
        onAddHandler = outlineEditMode.onAddEvent
    }
    if (setupEditMode?.onAddSetupEvent) {
        onAddHandler = setupEditMode.onAddSetupEvent
    }

    const timestamp = secondsToTimestamp(segment.offset)
    const hasCondition = segment.condition != null && segment.condition !== ""

    return (
        <div
            className="d-flex flex-column"
            data-test={`segment-item-${dataTest(segment.name)}`}
        >
            {showSegmentHeader &&
                <div className="d-flex justify-content-between">
                    <div className="d-flex">
                        <div className="border border-bottom-0 p-1 px-2 small d-flex align-items-center">
                            {`${timestamp} — ${segment.name}`}

                            {hasCondition && (
                                <WithTooltip place="top">
                                    {ttId => (
                                        <FontAwesomeIcon
                                            className="ml-2 text-muted small"
                                            icon={faCogs}
                                            data-for={ttId}
                                            data-tip={`Condition: ${segment.condition}`}
                                        />
                                    )}
                                </WithTooltip>
                            )}
                        </div>


                        {outlineEditMode &&
                            <EditSegmentTab
                                onClick={() => outlineEditMode.onEdit(segment.id)}
                            />
                        }
                    </div>

                    {(outlineEditMode || setupEditMode) &&
                        <AddEventToggleTab
                            open={addEventVisible}
                            toggle={toggleAddEventVisible}
                        />
                    }
                </div>
            }

            <div className="d-flex flex-column border bottom-separator-border alternate-bg-light">
                {(addEventVisible && (outlineEditMode || setupEditMode)) &&
                    <AddEventInputs
                        segment={segment}
                        onAdd={onAddHandler}
                    />
                }

                {events.length === 0
                    ? <NoEventsMessage />
                    : events.map(event => {

                        const props: React.ComponentProps<typeof FightEventItem> = {
                            ...eventProps,
                            fightEvent: {
                                ...event,
                                name: decorateNameWithOrdinal(event)
                            }
                        }

                        if (event.userCreated && setupEditMode) {
                            props.setupEditMode = {
                                onEditEvent: setupEditMode.onEditEvent
                            }
                        }

                        return (
                            <FightEventItem
                                key={event.id}
                                {...props}
                            />
                        )
                    })
                }
            </div>

            {(outlineEditMode && outlineEditMode.blockChildSegment(segment.id)) &&
                <div className="d-flex align-items-center mt-3" style={{ opacity: 0.5 }}>
                    <hr className="flex-grow-1 mr-2" />
                    <div
                        data-test={'edit-fight-cannot-fit-segment-alert'}
                        className="p-1 px-3 small text-muted font-italic flex-shrink-0"
                    >
                        {"Not enough space for a segment"}
                    </div>
                    <hr className="flex-grow-1 ml-2" />
                </div>
            }

            {(outlineEditMode && outlineEditMode.blockChildSegment(segment.id) === false) &&
                <div className="d-flex align-items-center mt-3">
                    <hr className="flex-grow-1 mr-2" />
                    <DivBtn onClick={() => outlineEditMode?.onAdd(segment.id)}>
                        <div className="p-1 px-3 small text-muted bg-light border flex-shrink-0">
                            {`Insert segment`}
                        </div>
                    </DivBtn>
                    <hr className="flex-grow-1 ml-2" />
                </div>
            }
        </div>
    )
}
